import { useEffect, useRef, useState } from 'react';
import { gamesService, type Game } from '../../services/games';
import {
    gameLanguagesService,
    type GameLanguage,
} from '../../services/gameLanguages';
import { GameCard } from '../../components/gameCard';
import { GamePopup } from '../../components/gamePopup';
import { Panel } from '../../components/ui/panel';
import { trackDemoView, trackGamesLoaded } from '../../helpers/analytics';
import { useGameLaunch } from '../../hooks/useGameLaunch';
import { useLauncherLocale } from '../../hooks/useLauncherLocale';
import { GamesFilterToolbar, useGamesFilter } from './components/gamesFilterToolbar';

import styles from './AccessLink.module.css';

export function AccessLink() {
    const locale = useLauncherLocale();
    const [games, setGames] = useState<Game[]>([]);
    const [gameLanguagesByGameId, setGameLanguagesByGameId] = useState<
        Record<string, GameLanguage>
    >({});
    const [error, setError] = useState<string | null>(null);
    const { handleLaunch, activeGame, handleClose } = useGameLaunch();
    const { filters, setFilters, clearFilters, filterOptions, filteredGames } =
        useGamesFilter(games);
    const demoViewTracked = useRef(false);
    const gamesLoadedTracked = useRef(false);

    useEffect(() => {
        if (demoViewTracked.current) {
            return;
        }
        demoViewTracked.current = true;
        trackDemoView();
    }, []);

    useEffect(() => {
        let isMounted = true;

        gamesService
            .getLiveGames()
            .then((items) => {
                if (!isMounted) {
                    return;
                }

                setGames(items);
                if (!gamesLoadedTracked.current) {
                    gamesLoadedTracked.current = true;
                    trackGamesLoaded(items);
                }
            })
            .catch((err) => {
                if (isMounted) {
                    setError(err instanceof Error ? err.message : 'Failed to load games');
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        gameLanguagesService
            .getLiveGameLanguagesByLocale(locale)
            .then((languagesByGameId) => {
                if (isMounted) {
                    setGameLanguagesByGameId(languagesByGameId);
                }
            })
            .catch((err) => {
                console.warn(
                    'Failed to load game languages',
                    err instanceof Error ? err.message : err
                );
                if (isMounted) {
                    setGameLanguagesByGameId({});
                }
            });

        return () => {
            isMounted = false;
        };
    }, [locale]);

    return (
        <div className={styles.container}>
            {error ? <p>{error}</p> : null}

            {games.length > 0 ? (
                <GamesFilterToolbar
                    filters={filters}
                    filterOptions={filterOptions}
                    visibleCount={filteredGames.length}
                    onFiltersChange={setFilters}
                    onClearFilters={clearFilters}
                />
            ) : null}

            {games.length > 0 && filteredGames.length === 0 ? (
                <Panel className={styles.noResults} role="status">
                    <p className={styles.noResultsTitle}>No games match</p>
                    <p className={styles.noResultsText}>
                        Try a different search term or clear filters to see all games.
                    </p>
                </Panel>
            ) : null}

            {filteredGames.length > 0 ? (
                <div className={styles.grid}>
                    {filteredGames.map((game) => (
                        <GameCard
                            key={game.id}
                            game={game}
                            gameLanguage={gameLanguagesByGameId[game.id] ?? null}
                            onLaunch={handleLaunch}
                        />
                    ))}
                </div>
            ) : null}

            {activeGame ? (
                <GamePopup
                    url={activeGame.url}
                    name={activeGame.name}
                    format={activeGame.format}
                    onClose={handleClose}
                />
            ) : null}
        </div>
    );
}
